import type { AuditChange, AuditLogDetail } from '@fos/shared/contracts/audit';
import { AUDIT_ACTION_GROUP_LABELS, AUDIT_ACTOR_TYPE_LABELS, VIA_LABELS } from '@fos/shared/labels';
import { ArrowRight, ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDate, formatTime } from '../../../../lib/format';
import { trpc } from '../../../../lib/trpc';
import { Avatar } from '../../../../ui/Avatar';
import { Sheet } from '../../../../ui/Dialog';
import { Mono } from '../../../../ui/Mono';
import { ErrorState, ListSkeleton } from '../../../../ui/States';
import { ClientDot } from '../../../clients';
import { actorName, hiddenText } from '../audit-ui';

/**
 * Szczegóły wpisu dziennika (panel boczny; na telefonie od dołu): kto, kiedy, kanał, obiekt z linkiem, urządzenie,
 * adres IP i id żądania, czytelny diff przed / po (pola po polsku; sekrety, długie teksty i kwoty bez uprawnienia
 * tylko opisane słowami) i bezpieczny kontekst z meta. Fokus po zamknięciu wraca na wiersz listy (Sheet).
 */
export function AuditEntrySheet({
  entryId,
  onOpenChange,
  side = 'right',
}: {
  entryId: string | null;
  onOpenChange: (open: boolean) => void;
  side?: 'right' | 'bottom';
}) {
  const query = trpc.audit.get.useQuery({ id: entryId ?? '' }, { enabled: Boolean(entryId), retry: false });
  const entry = query.data;
  return (
    <Sheet
      open={Boolean(entryId)}
      onOpenChange={onOpenChange}
      side={side}
      title={entry ? entry.actionLabel : 'Wpis dziennika'}
      description={entry ? `${formatDate(entry.at)}, ${formatTime(entry.at)}` : undefined}
    >
      {query.isPending ? (
        <ListSkeleton rows={4} />
      ) : query.isError ? (
        <ErrorState
          compact
          title="Nie udało się wczytać wpisu"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : entry ? (
        <EntryDetails entry={entry} />
      ) : null}
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-line py-2.5 sm:flex-row sm:gap-3">
      <dt className="shrink-0 text-caption text-fg-muted sm:w-[104px] sm:pt-px">{label}</dt>
      <dd className="min-w-0 text-body-s break-words text-fg">{children}</dd>
    </div>
  );
}

function EntryDetails({ entry }: { entry: AuditLogDetail }) {
  const viaLabel = VIA_LABELS[entry.via as keyof typeof VIA_LABELS] ?? entry.via;
  const actorType = AUDIT_ACTOR_TYPE_LABELS[entry.actorType as keyof typeof AUDIT_ACTOR_TYPE_LABELS];
  return (
    <div className="flex flex-col gap-6">
      <dl>
        <Row label="Kto">
          <span className="inline-flex items-center gap-2">
            {entry.actor ? (
              <Avatar initials={entry.actor.initials} color={entry.actor.avatarColor} title={entry.actor.name} />
            ) : (
              <Avatar ai={entry.actorType === 'ai'} initials="S" title={actorName(entry)} />
            )}
            <span>{actorName(entry)}</span>
            {entry.actor && entry.actorType !== 'user' && <span className="text-fg-muted">· {actorType}</span>}
          </span>
        </Row>
        <Row label="Kanał">{viaLabel}</Row>
        {entry.group && <Row label="Rodzaj">{AUDIT_ACTION_GROUP_LABELS[entry.group]}</Row>}
        {entry.objectLabel && (
          <Row label="Obiekt">
            {entry.objectPath ? (
              <Link
                to={entry.objectPath}
                className="inline-flex items-center gap-1 rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {entry.objectLabel}
                <ExternalLink size={14} strokeWidth={1.75} aria-hidden />
              </Link>
            ) : (
              entry.objectLabel
            )}
          </Row>
        )}
        {entry.client && (
          <Row label="Klient">
            <span className="inline-flex items-center gap-2">
              <ClientDot color={entry.client.color} />
              {entry.client.name}
            </span>
          </Row>
        )}
        {entry.device && <Row label="Urządzenie">{entry.device}</Row>}
        {entry.ip && (
          <Row label="Adres IP">
            <Mono>{entry.ip}</Mono>
          </Row>
        )}
        <Row label="Akcja">
          <Mono className="text-caption text-fg-secondary">{entry.action}</Mono>
        </Row>
        {entry.context.map((c) => (
          <Row key={c.label} label={c.label}>
            {c.value}
          </Row>
        ))}
        {entry.requestId && (
          <Row label="Żądanie">
            <Mono className="text-caption text-fg-secondary">{entry.requestId}</Mono>
          </Row>
        )}
      </dl>
      <section aria-label="Zmiany">
        <h3 className="text-label font-medium text-fg">Zmiany</h3>
        {entry.changes.length === 0 ? (
          <p className="mt-2 text-body-s text-fg-muted">
            {entry.detail ? `„${entry.detail}”` : 'Wpis bez zmienionych pól (zdarzenie).'}
          </p>
        ) : (
          <ul className="mt-2 flex flex-col">
            {entry.changes.map((c) => (
              <ChangeRow key={c.field} change={c} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChangeRow({ change }: { change: AuditChange }) {
  return (
    <li className="border-b border-line py-2.5">
      <p className="text-caption text-fg-muted">{change.label}</p>
      {change.hidden ? (
        <p className="mt-0.5 text-body-s text-fg-secondary italic">{hiddenText(change.hiddenReason)}</p>
      ) : (
        <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-body-s">
          <span className="text-fg-muted">
            <span className="sr-only">Przed: </span>
            {change.from ?? '—'}
          </span>
          <ArrowRight size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-fg-muted" />
          <span className="text-fg">
            <span className="sr-only">Po: </span>
            {change.to ?? '—'}
          </span>
        </p>
      )}
    </li>
  );
}
